import type { UseFormSetValue } from 'react-hook-form';
import {
  type RolesSchema,
  type StepsState,
} from '@/components/events/detail/tabs/contributors/tab-actions/add-edit-contributor/add-edit-contributor.schema';
import { FormControlLabel, FormGroup, Switch, FormControl, FormHelperText } from '@mui/material';
import { CREW_ROLES, type CrewRole, type ContributorDto } from '@/types/dtos/contributors/response/contributor.dto.ts';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions.ts';
import { useEventStore } from '@/state/stores/use-event-store.ts';
import { getObjectId } from '@/helpers/object.id.helper';
import { useAuth } from '@clerk/clerk-react';
import { useMemo } from 'react';
import { getAcceptedEventIdsForRoleMatch, matchesEventCrewEntity } from '@/utils/contributor-extraction.utils';
import { isContributorLinkedToClerkUser } from '@/utils/contributor-clerk-match.utils';

export default function EventRolesForm({
  eventId,
  roles,
  setValue,
  contributor,
}: {
  eventId: string;
  roles: RolesSchema[];
  setValue: UseFormSetValue<StepsState>;
  contributor?: ContributorDto;
}) {
  const t = useTranslations();
  const { userId } = useAuth();
  const { data: permissions } = usePermissionsQuery();
  const event = useEventStore((s) => s.event);
  const eventIdFromStore = event?.origin ? getObjectId(event.origin) : event?.id;
  const eventIdForPermissions = eventId || eventIdFromStore;

  // Check if user has owner role on event (using delete permission as proxy)
  // Backend will enforce actual owner check
  const hasOwnerRole = !!(eventIdForPermissions && permissions?.events?.[eventIdForPermissions]?.includes('delete'));

  const acceptedEventIds = useMemo(() => getAcceptedEventIdsForRoleMatch(event), [event]);
  const isSelf = isContributorLinkedToClerkUser(contributor, userId);

  const contributorHasPersistedEventCrewRole = (roleName: string): boolean => {
    if (!contributor?.roles?.length) return false;
    return contributor.roles.some(
      (r) =>
        !(r as { deleted?: boolean }).deleted &&
        r.entityType === 'Event' &&
        (r.role as string) === roleName &&
        matchesEventCrewEntity(r.entity, acceptedEventIds),
    );
  };

  const conflictingRoles = ['owner', 'event_manager'];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, role: string) => {
    const { checked } = e.target;
    if (checked) {
      const filteredRoles = roles.filter(
        (assignmentRole: { role: string }) => !conflictingRoles.includes(assignmentRole.role),
      );

      setValue('assignments.roles', [
        ...filteredRoles,
        {
          role: role as CrewRole,
          entity: eventId,
          entityType: 'Event',
        },
      ]);
    } else {
      setValue(
        'assignments.roles',
        roles.filter((assignmentRole: { role: string }) => assignmentRole.role !== role),
      );
    }
  };

  return (
    <FormGroup sx={{ m: '0.8rem 0 0 0.8rem' }}>
      {CREW_ROLES.map((role) => {
        const isRestrictedRole = role === 'event_manager' || role === 'owner';
        const restrictedForNonOwner = isRestrictedRole && !hasOwnerRole;
        const cannotRemoveOwnRole = isSelf && contributorHasPersistedEventCrewRole(role);
        const anotherCrewRoleSelected = roles.some(
          (assignmentRole) => conflictingRoles.includes(assignmentRole.role) && assignmentRole.role !== role,
        );
        const isDisabled = restrictedForNonOwner || cannotRemoveOwnRole || anotherCrewRoleSelected;

        return (
          <FormControl key={role} sx={{ mb: 1.5 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={roles.some((assignmentRole: { role: string }) => assignmentRole.role === role)}
                  onChange={(event) => handleChange(event, role)}
                  disabled={isDisabled}
                />
              }
              label={t(`common.roleLabels.${role}`)}
            />
            {cannotRemoveOwnRole && !restrictedForNonOwner ? (
              <FormHelperText sx={{ ml: 6 }}>{t('event.detail.contributors.cannotRemoveOwnRole')}</FormHelperText>
            ) : null}
            {t(`common.roleSubtitles.${role}`, { defaultValue: '' }) ? (
              <FormHelperText sx={{ ml: 6 }}>{t(`common.roleSubtitles.${role}`)}</FormHelperText>
            ) : null}
          </FormControl>
        );
      })}
    </FormGroup>
  );
}
